import React, { useState, useEffect } from "react"
import {
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableBody,
  Button,
  CircularProgress,
  Modal,
  Typography,
  Box,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Step,
  Stepper,
  StepLabel,
} from "@mui/material"
import PaginationTable from "../../Pagination"
import {
  cancelSalaryCertificateRequest,
  fetchEmpSalaryCertificates
} from "../../../Actions/commonController"
import { useAuth } from "../../../Context/authContext"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty"
import CancelIcon from "@mui/icons-material/Cancel"
import { FormatDate } from "../../Utilities/DateFormatter"
import { toast } from "react-toastify"


const columns = [
  { id: "ID", label: "SR NO.", minWidth: 80 },
  { id: "requestDate", label: "REQUEST DATE", minWidth: 100 },
  { id: "towhom", label: "TO WHOM", minWidth: 100 },
  { id: "country", label: "COUNTRY", minWidth: 100 },
  { id: "status", label: "STATUS", minWidth: 100 },
  { id: "reason", label: "REASON", minWidth: 100 },
  { id: "actions", label: "ACTIONS", minWidth: 200 },
]

export default function MySalaryCertificatesTable() {
  const [page, setPage] = useState(1)
  const [quantity, setQuantity] = useState(10)
  const [totalItems, setTotalItems] = useState(0)
  const [data, setData] = useState([])
  const [selectedReason, setSelectedReason] = useState("")
  const [reasonModalOpen, setReasonModalOpen] = useState(false)
  const [trackerModalOpen, setTrackerModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loader, setLoader] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedRequestId, setSelectedRequestId] = useState(null)
  const [statusFilter, setStatusFilter] = useState("all")
  const { eid } = useAuth()
  const [rejectModalOpen, setRejectModalOpen] = useState(false)
  const [selectedRejectInfo, setSelectedRejectInfo] = useState({
    rejectedByName: "",
    rejectedreason: "",
  })
  const [trackerData, setTrackerData] = useState({
    pending: [],
    accepted: [],
    rejected: [],
  })

  const fetchRequests = async () => {
    try {
      const APIData = {
        empId: eid,
        quantity: quantity,
        page: page,
        status: statusFilter,
      }
      setLoader(true)
      const { data, status } = await fetchEmpSalaryCertificates(APIData)
      if (status === "success") {
        setData(data)
        setTotalItems(data.length)
      } else {
        console.error("Error fetching salary certificates", status)
      }
    } catch (error) {
      console.error("Error in fetchRequests function", error)
    }
    setReasonModalOpen(false)
    setLoader(false)
  }

  const handleConfirmDialog = async () => {
    setLoading(true)
    try {
      let APIData = {
        salaryReqId: selectedRequestId,
      }
      const { status } = await cancelSalaryCertificateRequest(APIData)
      if (status === "success") {
        fetchRequests()
        toast.success("Salary Certificate request cancelled!")
      } else {
        console.error("Error cancelling request", status)
      }
    } catch (error) {
      console.error("Error cancelling request", error)
    }
    setLoading(false)
    setDialogOpen(false)
    setSelectedRequestId(null)
  }

  const handleFilter = (status) => {
    setStatusFilter(status)
    setPage(1)
  }

  const handlePageClick = async (newPage) => {
    setPage(newPage)
  }

  const handleItemsPerPageChange = async (newRowsPerPage) => {
    setQuantity(newRowsPerPage)
    setPage(1)
  }

  const handleRejectedInfo = (rejectedByName, rejectedreason) => {
    setSelectedRejectInfo({ rejectedByName, rejectedreason })
    setRejectModalOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setSelectedRequestId(null)
  }

  const handleCloseModal = () => {
    setReasonModalOpen(false)
    setTrackerModalOpen(false)
    setRejectModalOpen(false)
  }

  const handleCancelClick = (requestId) => {
    setSelectedRequestId(requestId)
    setDialogOpen(true)
  }

  const handleReasonButton = (reason) => {
    setSelectedReason(reason)
    setReasonModalOpen(true)
  }

  useEffect(() => {
    fetchRequests()
  }, [eid, statusFilter, page, quantity])

  const chipColor = (status) => {
    switch (status) {
      case "Pending":
        return "primary"
      case "Cancelled":
        return "warning"
      case "Rejected":
        return "error"
      case "accepted":
        return "success"
      default:
        return "default"
    }
  }

  const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase()
  }

  const handleTrackerButtonClick = (item) => {
    const tracker = {
      pending: item.pendingNames ? item.pendingNames.split(",") : [],
      accepted: item.acceptedNames ? item.acceptedNames.split(",") : [],
      rejected: item.rejectedNames ? item.rejectedNames.split(",") : [],
    }
    setTrackerData(tracker)
    setTrackerModalOpen(true)
  }

  const renderTracker = () => {
    const steps = [
      ...trackerData.accepted.map((name) => ({ name, status: "accepted" })),
      ...trackerData.pending.map((name) => ({ name, status: "pending" })),
      ...trackerData.rejected.map((name) => ({ name, status: "rejected" })),
    ]

    return (
      <Box sx={{ width: "100%", marginTop: 2 }}>
        <Stepper alternativeLabel>
          {steps.map((step, index) => (
            <Step key={index} active>
              <StepLabel
                StepIconComponent={() =>
                  step.status === "accepted" ? (
                    <CheckCircleIcon sx={{ color: "var(--secondary-color)" }} />
                  ) : step.status === "rejected" ? (
                    <CancelIcon color="error" />
                  ) : (
                    <HourglassEmptyIcon color="warning" />
                  )
                }
              >
                {step.name}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
    )
  }

  return (
    <Paper className="pagePaper">
      <span className="info">
        <h5>My Salary Certificate Requests</h5>
      </span>
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "30px" }}>
        <Button
          variant="contained"
          onClick={() => handleFilter("all")}
          sx={{
            backgroundColor: statusFilter === "all" ? "var(--secondary-color)" : "transparent",
            color: statusFilter === "all" ? "white" : "var(--secondary-color)",
            marginRight: "10px",
            "&:hover": {
              backgroundColor: "var(--secondary-color)",
              color: "white",
            },
          }}
        >
          All
        </Button>

        <Button
          variant="contained"
          onClick={() => handleFilter("Pending")}
          sx={{
            backgroundColor: statusFilter === "Pending" ? "var(--secondary-color)" : "transparent",
            color: statusFilter === "Pending" ? "white" : "var(--secondary-color)",
            marginRight: "10px",
            "&:hover": {
              backgroundColor: "var(--secondary-color)",
              color: "white",
            },
          }}
        >
          Pending
        </Button>

        <Button
          variant="contained"
          onClick={() => handleFilter("Accepted")}
          sx={{
            backgroundColor: statusFilter === "Accepted" ? "var(--secondary-color)" : "transparent",
            color: statusFilter === "Accepted" ? "white" : "var(--secondary-color)",
            marginRight: "10px",
            "&:hover": {
              backgroundColor: "var(--secondary-color)",
              color: "white",
            },
          }}
        >
          Accepted
        </Button>

        <Button
          variant="contained"
          onClick={() => handleFilter("Rejected")}
          sx={{
            backgroundColor: statusFilter === "Rejected" ? "var(--secondary-color)" : "transparent",
            color: statusFilter === "Rejected" ? "white" : "var(--secondary-color)",
            marginRight: "10px",
            "&:hover": {
              backgroundColor: "var(--secondary-color)",
              color: "white",
            },
          }}
        >
          Rejected
        </Button>

        <Button
          variant="contained"
          onClick={() => handleFilter("Cancelled")}
          sx={{
            backgroundColor: statusFilter === "Cancelled" ? "var(--secondary-color)" : "transparent",
            color: statusFilter === "Cancelled" ? "white" : "var(--secondary-color)",
            "&:hover": {
              backgroundColor: "var(--secondary-color)",
              color: "white",
            },
          }}
        >
          Cancelled
        </Button>
      </div>

      <div className="row" style={{
        paddingTop: "60px",
      }}>
        <div className="col-md-12">
          <TableContainer
            sx={{
              overflowY: "auto",
              maxHeight: "500px"
            }}
          >
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      className="tableHeader"
                      key={column.id}
                      align="center"
                      style={{
                        minWidth: column.minWidth,
                        backgroundColor: "var(--card-color)",
                        color: "var(--primary-text-color)",
                      }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {loader ? (
                  <TableRow>
                    <TableCell colSpan={11} style={{ padding: "30px 0", textAlign: "center" }}>
                      <CircularProgress size={50} style={{ color: "var(--secondary-color)" }} />
                    </TableCell>
                  </TableRow>
                ) : data && data.length > 0 ? (
                  data.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell align="center">
                        {index === data.length - 1 ? 1 : data.length - index}
                      </TableCell>
                      <TableCell align="center">{FormatDate(item.createdat)}</TableCell>
                      <TableCell align="center">{item.towhom}</TableCell>
                      <TableCell align="center">{item.country}</TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <Chip label={capitalizeFirstLetter(item.status)} color={chipColor(item.status)} />
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        <Button variant="contained" className="tableButton" onClick={() => handleReasonButton(item.reason)}>
                          View
                        </Button>
                      </TableCell>
                      <TableCell align="center">
                        {item.status === "Pending" ? (
                          <>
                            <Button variant="contained" className="btn cancel" onClick={() => handleCancelClick(item.id)}>
                              Cancel
                            </Button>
                            <Button variant="contained" className="btn tracker" onClick={() => handleTrackerButtonClick(item)}>
                              Tracker
                            </Button>
                          </>
                        ) : item.status === "Rejected" ? (
                          <Button variant="contained" className="btn rjct" onClick={() => handleRejectedInfo(item.rejectedByName, item.rejectedreason)}>
                            Info
                          </Button>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={11} style={{ textAlign: "center", color: "var(--text-primary)" }}>No data found</TableCell>
                  </TableRow>
                )}
              </TableBody>

            </Table>
          </TableContainer>
        </div>
      </div>

      {/* Cancel Modal*/}
      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>Cancel Salary Certificate Request</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel this request?
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "space-between",
            padding: "20px",
          }}
        >
          <Button
            style={{
              backgroundColor: "#FF0000",
              color: "white",
              width: "60px",
              height: "35px",
            }}
            onClick={handleCloseDialog}
            autoFocus
            disabled={loading}
          >
            No
          </Button>
          <Button
            sx={{
              backgroundColor: 'var(--secondary-color)',
              color: 'white',
              width: '60px',
              height: '35px',
            }}
            onClick={handleConfirmDialog}
            disabled={loading}
            autoFocus
          >
            {loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : "Yes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reason Modal */}
      <Modal
        open={reasonModalOpen}
        onClose={handleCloseModal}
        aria-labelledby="reason-modal-title"
        aria-describedby="reason-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "var(--card-color)",
            border: "none",
            boxShadow: 24,
            borderRadius: 2,
            p: 4,
          }}
        >
          <Typography id="reason-modal-title" variant="h6" component="h2">
            Reason
          </Typography>
          <Typography id="reason-modal-description" sx={{ mt: 2 }}>
            {selectedReason}
          </Typography>
        </Box>
      </Modal>

      {/* Rejection Info Modal */}
      <Modal
        open={rejectModalOpen}
        onClose={handleCloseModal}
        aria-labelledby="rejection-modal-title"
        aria-describedby="rejection-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "var(--card-color)",
            borderRadius: 2,
            border: "none",
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography id="rejection-modal-title" variant="h6" component="h2">
            Reason for Rejection
          </Typography>
          <Typography id="rejection-modal-description" sx={{ mt: 2 }}>
            {selectedRejectInfo.rejectedreason}
          </Typography>
          <Typography sx={{ mt: 2, fontStyle: "italic" }}>
            Rejected by: {selectedRejectInfo.rejectedByName}
          </Typography>
        </Box>
      </Modal>

      {/* Tracker Modal */}
      <Modal
        open={trackerModalOpen}
        onClose={handleCloseModal}
        aria-labelledby="tracker-modal-title"
        aria-describedby="tracker-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "var(--card-color)",
            border: "none",
            boxShadow: 24,
            borderRadius: 2,
            p: 4,
          }}
        >
          <Typography id="tracker-modal-title" variant="h6" component="h2">
            Approval Tracker
          </Typography>
          {renderTracker()}
        </Box>
      </Modal>

      <PaginationTable
        totalItems={totalItems}
        itemsPerPage={quantity}
        onPageChange={handlePageClick}
        onItemsPerPageChange={handleItemsPerPageChange}
      />
    </Paper>
  )
}